import { PlaylistGroupProps } from '@osmosis/shared/types/playlist';
import { PlaylistsRes } from '@osmosis/shared/types/playlist';
import { PlaylistActionType } from '@osmosis/shared/types/playlist';
import { PlaylistCreateRes } from '@osmosis/shared/types/playlist';
import { PlaylistType } from '@osmosis/shared/types/playlist';
import { PlaylistStateType } from '@osmosis/shared/types/playlist';
import {
  PlaylistProps,
  ServerPlaylistProps,
} from '@osmosis/shared/types/playlist';

import { getPlaylistPayloadWithNodes } from '../../playlist';
import { groupPlaylists } from '../../shared/playlist/utils';
import { AxiosGetType } from '../../../context/FetchProvider';
import { AxiosPostType } from '../../../context/FetchProvider';

const defaultPlaylist: PlaylistProps = {
  followers: [],
  list: [],
  quizAllData: [],
  created: '',
  modified: '',
  playlistID: 0,
  progress: 0,
  title: '',
  type: PlaylistType.WATCH_LATER,
  url: '',
  userID: null,
  user: {
    preferredName: '',
    lastName: '',
  },
  userOwned: false,
  media: {
    ques: {},
    fc: [],
  },
};

const defaultPlaylistAddVideoResponse = {
  playlist: defaultPlaylist,
  success: 1 as PlaylistStateType,
};

type AddToPlaylistArgs = {
  action: PlaylistActionType;
  nodeIDs: number[];
  url?: string;
  type?: PlaylistType;
  addQuizData?: boolean;
};

export const emptyPlaylistResult = {
  playlists: [],
  savedPlaylists: {
    follow: [],
  },
  success: 0 as PlaylistStateType,
};

/**
 * Fetch user playlists.
 */
export const fetchUserPlaylists = async (
  get: AxiosGetType,
): Promise<PlaylistGroupProps[]> => {
  try {
    const res = await get<PlaylistsRes>('/v0/playlists');

    return groupPlaylists(res.data.playlists || []);
  } catch {
    return [];
  }
};

/**
 * Fetch a list of the user's playlists.
 */
export const fetchUserPlaylistTitles = async (
  get: AxiosGetType,
): Promise<PlaylistGroupProps[]> => {
  try {
    const res = await get<PlaylistsRes>('/v0/playlist_list');

    return groupPlaylists(res.data.playlists || []);
  } catch {
    return [];
  }
};

/**
 * Fetch a single playlist by its url.
 */
export const fetchPlaylistByUrl = async (
  get: AxiosGetType,
  url: string,
): Promise<PlaylistProps | null> => {
  try {
    const res = await get<ServerPlaylistProps>(
      `/v0/playlist?url=${encodeURIComponent(url)}`,
    );

    return res.data.playlist || null;
  } catch {
    return null;
  }
};

/**
 * Delete user playlist.
 */
export const deleteUserPlaylist = async (
  post: AxiosPostType,
  playlistUrl: string,
) => {
  try {
    const res = await post<PlaylistsRes, { url: string }>(
      '/deleteUserPlaylist',
      {
        url: playlistUrl,
      },
    );

    return res?.data;
  } catch {
    return emptyPlaylistResult;
  }
};

/**
 * Add videos to playlist.
 */
export const addToPlaylist = async (
  post: AxiosPostType,
  args: Omit<AddToPlaylistArgs, 'action'> & { special?: number },
) => {
  try {
    const { nodeIDs: videos, addQuizData, ...rest } = args;
    const res = await post<
      { success: PlaylistStateType; playlist: PlaylistProps },
      {
        url?: string;
        action: 'add-many';
        videos: number[];
        type?: PlaylistType;
        special?: number;
        addQuizData?: boolean;
      }
    >('/editUserPlaylist', {
      action: 'add-many',
      videos,
      addQuizData,
      ...rest,
    });

    return res?.data;
  } catch {
    return defaultPlaylistAddVideoResponse;
  }
};

/**
 * Copy videos to another playlist.
 */
export const copyToAnotherPlaylist = async (
  post: AxiosPostType,
  args: Omit<AddToPlaylistArgs, 'action'>,
) => {
  try {
    const { nodeIDs, url, type } = args;
    const isNodesMany = nodeIDs.length > 1;
    const res = await post<
      PlaylistsRes,
      {
        url?: string;
        action: 'add-many' | 'add';
        type?: PlaylistType;
      }
    >('/editUserPlaylist', {
      url,
      action: isNodesMany ? 'add-many' : 'add',
      type,
      ...getPlaylistPayloadWithNodes(nodeIDs),
    });

    return res?.data;
  } catch {
    return emptyPlaylistResult;
  }
};

/**
 * Remove videos from playlist.
 */
export const reorderPlaylistItems = async (
  post: AxiosPostType,
  args: Omit<AddToPlaylistArgs, 'action'>,
) => {
  try {
    const { nodeIDs, url } = args;
    const res = await post<
      PlaylistsRes,
      { url?: string; videos: number[]; action: 'reset' }
    >('/editUserPlaylist', {
      url,
      videos: nodeIDs,
      action: 'reset',
    });

    return res?.data;
  } catch {
    return emptyPlaylistResult;
  }
};

/**
 * Remove videos from playlist.
 */
export const removeFromPlaylist = async (
  post: AxiosPostType,
  args: Omit<AddToPlaylistArgs, 'action'>,
) => {
  try {
    const { nodeIDs, url, ...rest } = args;
    const isNodesMany = nodeIDs.length > 1;
    const res = await post<
      PlaylistsRes,
      {
        url?: string;
        action: 'remove-many' | 'remove';
        type?: PlaylistType;
      }
    >('/editUserPlaylist', {
      url,
      action: isNodesMany ? 'remove-many' : 'remove',
      ...rest,
      ...getPlaylistPayloadWithNodes(nodeIDs),
    });

    return res?.data;
  } catch {
    return emptyPlaylistResult;
  }
};

/**
 * Create playlist.
 */
export const createPlaylist = async (post: AxiosPostType, title: string) => {
  try {
    const res = await post<PlaylistCreateRes, { title: string }>(
      '/createUserPlaylist',
      {
        title,
      },
    );

    return res?.data;
  } catch {
    return { ...emptyPlaylistResult, alreadyExists: false, url: '' };
  }
};

/**
 * Rename playlist.
 */
export const renamePlaylist = async (
  post: AxiosPostType,
  newTitle: string,
  playlistUrl: string,
) => {
  try {
    const res = await post<
      PlaylistsRes,
      { url: string; action: 'rename'; newName: string }
    >('/editUserPlaylist', {
      url: playlistUrl,
      action: 'rename',
      newName: newTitle,
    });

    return res?.data;
  } catch {
    return emptyPlaylistResult;
  }
};

/**
 * Follow playlist.
 */
export const followPlaylist = async (
  post: AxiosPostType,
  playlistUrl: string,
  saveType: PlaylistStateType,
) => {
  try {
    const res = await post<
      PlaylistsRes,
      { url: string; saveType: PlaylistStateType }
    >('/saveUserPlaylist', {
      url: playlistUrl,
      saveType,
    });

    return res?.data;
  } catch {
    return emptyPlaylistResult;
  }
};

/**
 * Fetch user playlists.
 */
export const getUserPlaylists = async (
  post: AxiosPostType,
): Promise<PlaylistsRes> => {
  const res = await post<PlaylistsRes, null>('/getUserPlaylists');
  return res.data;
};
